import React from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  AreaChart,
  Area
} from 'recharts';

interface ChartData {
  type: string;
  data: any[];
  labels?: string[];
  series: any[];
  totalRecords: number;
  availableFields: string[];
  fieldAnalysis?: any;
  suggestedChartTypes?: string[];
  message?: string;
  value?: number;
  label?: string;
}

interface ChartRendererProps {
  chartData: ChartData;
}

const COLORS = [
  '#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1',
  '#d084d0', '#ffb347', '#87ceeb', '#dda0dd', '#98fb98'
];

const ChartRenderer: React.FC<ChartRendererProps> = ({ chartData }) => {
  if (!chartData) {
    return null;
  }

  // Handle metric type (fallback case)
  if (chartData.type === 'metric') {
    return (
      <div className="bg-gray-50 rounded-lg p-6 my-4 border border-gray-200">
        <div className="text-center">
          <div className="text-3xl font-bold text-blue-600 mb-2">
            {chartData.value?.toLocaleString() || chartData.totalRecords}
          </div>
          <div className="text-gray-600 text-sm">
            {chartData.label || 'Total Records'}
          </div>
          {chartData.message && (
            <div className="text-gray-500 text-xs mt-2">
              {chartData.message}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Handle empty or null chart data
  if (!chartData.series || chartData.series.length === 0) {
    return (
      <div className="bg-gray-50 rounded-lg p-4 my-4 border border-gray-200">
        <div className="text-center text-gray-500 text-sm">
          No chart data available
        </div>
      </div>
    );
  }

  const renderChart = () => {
    switch (chartData.type) {
      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chartData.series[0]?.data || []}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ label, percent }) => `${label} (${(percent * 100).toFixed(0)}%)`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {(chartData.series[0]?.data || []).map((_: any, index: number) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        );

      case 'bar':
        // Prepare data for bar chart
        const barData = chartData.labels 
          ? chartData.labels.map((label, index) => ({
              name: label,
              value: chartData.series[0]?.data[index] || 0
            }))
          : chartData.series[0]?.data?.map((value: number, index: number) => ({
              name: `Item ${index + 1}`,
              value
            })) || [];

        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={barData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="value" fill="#8884d8" />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'line':
        // Prepare data for line chart
        const lineData = chartData.series[0]?.data?.map((point: any) => ({
          x: point.x,
          y: point.y,
          name: new Date(point.x).toLocaleDateString()
        })) || [];

        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={lineData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="y" 
                stroke="#8884d8" 
                strokeWidth={2}
                name={chartData.series[0]?.label || 'Value'}
              />
            </LineChart>
          </ResponsiveContainer>
        );

      case 'area':
        const areaData = chartData.series[0]?.data?.map((point: any) => ({
          x: point.x,
          y: point.y,
          name: new Date(point.x).toLocaleDateString()
        })) || [];

        return (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={areaData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Area 
                type="monotone" 
                dataKey="y" 
                stroke="#8884d8" 
                fill="#8884d8"
                fillOpacity={0.3}
                name={chartData.series[0]?.label || 'Value'}
              />
            </AreaChart>
          </ResponsiveContainer>
        );

      case 'scatter':
        const scatterData = chartData.series[0]?.data?.map((point: any) => ({
          x: point.x,
          y: point.y
        })) || [];

        return (
          <ResponsiveContainer width="100%" height={300}>
            <ScatterChart data={scatterData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="x" />
              <YAxis dataKey="y" />
              <Tooltip />
              <Legend />
              <Scatter 
                dataKey="y" 
                fill="#8884d8"
                name={chartData.series[0]?.label || 'Data Points'}
              />
            </ScatterChart>
          </ResponsiveContainer>
        );

      default:
        // Fallback to bar chart for unknown types
        const defaultData = chartData.labels 
          ? chartData.labels.map((label, index) => ({
              name: label,
              value: chartData.data[index] || 0
            }))
          : [{ name: 'No Data', value: 0 }];

        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={defaultData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="value" fill="#8884d8" />
            </BarChart>
          </ResponsiveContainer>
        );
    }
  };

  return (
    <div className="bg-white rounded-lg p-4 my-4 border border-gray-200 shadow-sm">
      <div className="mb-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-gray-700 capitalize">
            {chartData.type} Chart
          </h4>
          <div className="text-xs text-gray-500">
            {chartData.totalRecords} records
          </div>
        </div>
        {chartData.suggestedChartTypes && chartData.suggestedChartTypes.length > 1 && (
          <div className="text-xs text-gray-400 mt-1">
            Other options: {chartData.suggestedChartTypes.filter(t => t !== chartData.type).join(', ')}
          </div>
        )}
      </div>
      
      <div className="chart-container">
        {renderChart()}
      </div>
      
      {chartData.availableFields && chartData.availableFields.length > 0 && (
        <div className="mt-3 text-xs text-gray-400">
          Fields: {chartData.availableFields.join(', ')}
        </div>
      )}
    </div>
  );
};

export default ChartRenderer;